import React from 'react';
import { createPortal } from 'react-dom';
import { PositionStatusEnum } from '../../../../generated-src/client/positions/api';

interface VacancyStatusDropdownProps {
  status: PositionStatusEnum;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  onStatusChange: (status: PositionStatusEnum) => void;
  getStatusLabel: (status: PositionStatusEnum) => string;
  getStatusColor: (status: PositionStatusEnum) => string;
}

export const VacancyStatusDropdown: React.FC<VacancyStatusDropdownProps> = ({
  status,
  isOpen,
  onToggle,
  onClose,
  onStatusChange,
  getStatusLabel,
  getStatusColor,
}) => {
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const [position, setPosition] = React.useState<{ top: number; right: number } | null>(null);

  React.useEffect(() => {
    if (!isOpen) return;

    const updatePosition = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;

      setPosition({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right,
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen]);

  const menu = isOpen ? (
    <>
      <div
        className="fixed inset-0 z-[99998]"
        data-testid="vacancy-status-dropdown-backdrop"
        onClick={onClose}
      />
      <div
        data-dropdown="vacancy-status"
        data-testid="vacancy-status-dropdown-menu"
        className="fixed w-48 bg-white rounded-2xl shadow-lg border border-[#e2e4e9] z-[99999] overflow-hidden"
        style={position ? { top: `${position.top}px`, right: `${position.right}px` } : { visibility: 'hidden' }}
      >
        {Object.values(PositionStatusEnum).map((item) => (
          <button
            key={item}
            type="button"
            onClick={() => onStatusChange(item)}
            className="w-full flex items-center gap-2 px-4 py-2 text-left hover:bg-[#f6f8fa] transition-colors text-sm"
          >
            <div
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: getStatusColor(item) }}
            />
            <span>{getStatusLabel(item)}</span>
          </button>
        ))}
      </div>
    </>
  ) : null;

  return (
    <div className="relative" data-dropdown="vacancy-status">
      <button
        ref={triggerRef}
        type="button"
        onClick={onToggle}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        data-testid="vacancy-status-dropdown-trigger"
        className="bg-[#ffffff] box-border content-stretch flex flex-row gap-2 items-center justify-center overflow-clip h-11 px-3 relative rounded-2xl shrink-0 hover:bg-[#f6f8fa] transition-colors"
      >
        <div
          className="w-3 h-3 rounded-full"
          style={{ backgroundColor: getStatusColor(status) }}
        />
        <span className="font-inter font-medium text-[#525866] text-[14px] text-center text-nowrap">
          {getStatusLabel(status)}
        </span>
        <svg className="w-5 h-5" fill="none" viewBox="0 0 16 16" stroke="currentColor" strokeWidth="1.5">
          <path d="M4 6L8 10L12 6" />
        </svg>
      </button>

      {menu ? createPortal(menu, document.body) : null}
    </div>
  );
};
